import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { updateConsultation, apiGetNoteTypes } from '../../../../routes/doctor/consultation';
import { updateDoctorPreferences } from '../../../../routes/doctor/patients';
import { setIsNoteTypePopupOpened, setNoteType } from '../../../../redux/features/doctor/Dashboard/consultation/consultation';
import { setNotificationMessage } from '../../../../redux/features/general/notification';
import LanguageRow from './items/LanguageRow';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import Edit from '@mui/icons-material/Edit';
import CancelButton from '../../../general/inputs/CancelButton';

interface NoteTypeOption {
    id: string;
    name: string;
    isSystemType: boolean;
}

const NoteType: React.FC = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const isMobile = useMediaQuery('(max-width: 768px)');
    const { consultationId } = useParams();
    const colors = useAppSelector(state => state.theme.colors);
    const currentNoteType = useAppSelector(state => state.consultation.noteType);

    const [noteTypes, setNoteTypes] = useState<NoteTypeOption[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchNoteTypes = async () => {
            try {
                const res = await apiGetNoteTypes();
                if (res.data.status === 200) {
                    setNoteTypes(res.data.noteTypes);
                }
            } catch (error) {
                console.error("Failed to fetch note types:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchNoteTypes();
    }, []);

    const getDisplayName = (noteType: NoteTypeOption) => {
        if (noteType.isSystemType) {
            const translationKey = `noteType_${noteType.id}`;
            return t(translationKey, noteType.name);
        }
        return noteType.name;
    };

    const handleNoteType = async (selectedNoteType: NoteTypeOption) => {
        try {
            const displayName = getDisplayName(selectedNoteType);
            
            // API calls still use the ID
            const consultationUpdateContent = { consultationId, noteType: selectedNoteType.id };
            await updateConsultation(consultationUpdateContent);

            const preferencesUpdateContent = { defaultNoteType: selectedNoteType.id };
            await updateDoctorPreferences(preferencesUpdateContent);

            // Redux gets the full object with the human-readable name
            dispatch(setNoteType({ id: selectedNoteType.id, name: displayName }));
            dispatch(setNotificationMessage(t('noteTypeUpdated')));
            dispatch(setIsNoteTypePopupOpened(false));
        } catch (error) {
            console.error('Error updating default note type:', error);
            dispatch(setNotificationMessage(t('noteTypeUpdateFailed')));
        }
    };

    const handleQuit = () => {
        dispatch(setIsNoteTypePopupOpened(false));
    };

    const handleManageTemplates = () => {
        navigate('/dashboard/templates');
        handleQuit();
    };

    return (
        <Box onClick={handleQuit} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100vw', height: '100vh', backgroundColor: colors?.popup, overflow: 'hidden', position: 'absolute', zIndex: 100000000 }}>
            <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', maxWidth: isMobile ? '90vw' : '400px', width: '100%', padding: 0, borderRadius: 4, backgroundColor: colors?.container, overflow: 'hidden' }}>
                <Box sx={{ width: '100%', fontSize: 18, fontWeight: 'bold', color: colors?.text, p: 2, borderBottom: `1px solid ${colors?.folderDataRowBorderColor}` }}>
                    {t('selectNoteType')}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', maxHeight: '50vh', overflowY: 'auto', scrollbarWidth: 'none', p: 2 }}>
                    {isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                            <CircularProgress sx={{ color: colors?.lightBlue }} />
                        </Box>
                    ) : noteTypes.length > 0 ? (
                        noteTypes.map((type) => (
                            <LanguageRow
                                key={type.id}
                                icon={<DescriptionOutlinedIcon />}
                                title={getDisplayName(type)}
                                handler={() => handleNoteType(type)}
                                isActive={currentNoteType?.id === type.id}
                            />
                        ))
                    ) : (
                        <Typography sx={{ color: colors?.listInfo, my: 3 }}>
                            {t('noNoteTypesFound')}
                        </Typography>
                    )}
                </Box>
                <Box sx={{ width: '100%', p: 2, borderTop: `1px solid ${colors?.folderDataRowBorderColor}` }}>
                    <Button
                        fullWidth
                        variant="contained"
                        startIcon={<Edit />}
                        onClick={handleManageTemplates}
                        sx={{
                            backgroundColor: colors?.lightBlue,
                            '&:hover': { backgroundColor: colors?.darkBlue },
                            fontWeight: 'bold',
                            borderRadius: 2,
                            py: 1.5
                        }}
                    >
                        {t('templates')}
                    </Button>
                </Box>
            </Box>
            <CancelButton handler={handleQuit} />
        </Box>
    );
};

export default NoteType;